import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../style/SearchPage.css';
import '../style/HomePage.modern.css';
import TopicsGrid from './TopicsGrid';
import UploadedFilesList from './UploadedFilesList';
import Navbar from './Navbar';
import LoginModal from './LoginModal';
import { useNotification } from 'rc-notification';
import { motion } from '../helpers/motion';
import NotificationContainer from './NotificationContainer';
import { useCloseModalListener } from '../hooks/useCloseModalListener';
import constants from '../helpers/constants';
import { setCookieBase64 } from './setCookieBase64';
import getApiHeaders from '../helpers/apiHeaders.js';
import { useLocation } from 'react-router-dom';
import { IoCloseCircle } from 'react-icons/io5';
import { FaPaperclip, FaSearch } from 'react-icons/fa';
import usePdfProcessor from '../hooks/usePdfProcessor';
import { notifyAndInviteToSubscribe } from './notifyAndInviteToSubscribe';
import SettingsModal from './SettingsModal';
import { useTranslation } from 'react-i18next';

const HomePage = () => {
  const location = useLocation();

  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const { processPdf } = usePdfProcessor();
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const fileInputRef = useRef(null);
  const confirmationCheckedRef = useRef(false);

  useCloseModalListener([setShowLoginModal, setShowSettingsModal]);

  const [notice, contextHolder] = useNotification({
    showProgress: true,
    getContainer: () => document.getElementById('notification-container'),
    motion,
  });

useEffect(() => {
    if (confirmationCheckedRef.current) return;
    const queryParams = new URLSearchParams(location.search);
    const accountConfirmationSucces = queryParams.get('accountConfirmationSucces')
    if (accountConfirmationSucces) {
      confirmationCheckedRef.current = true;
      const updateSession = async () => {
        try {
          const response = await fetch(
            `${constants.fetchAuthRefresh}`,
            { headers: getApiHeaders(), credentials: "include" });

          if (response.ok) {
            const result = await response.json();
            setCookieBase64("auth", result, { days: 1 })
            notice.open({
              content: (
                <div>
                  <div className="d-flex justify-content-between px-2 mb-2">
                    <small className="tag">{t('common.phew')}</small>
                    <strong>{t('home.accountConfirmationTitle')}</strong>
                  </div>
                  <div>{t('home.accountConfirmationMessage')}</div>
                </div>
              ),
              duration: 30,
              closable: true,
              showProgress: false,
              closeIcon: <IoCloseCircle />,
            });
          }
        } catch (error) {
          console.error("Error from server:", error);
        }
      }
      updateSession();
    }
  }, [location.search, notice, t])

  // Scroll animation for content sections
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('.content-section');
    sections.forEach(section => {
      section.style.opacity = '0';
      section.style.transform = 'translateY(40px)';
      section.style.transition = 'opacity 0.4s ease-out, transform 0.4s ease-out';
      observer.observe(section);
    });

    return () => {
      sections.forEach(section => observer.unobserve(section));
    };
  }, [])

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      const savedTopics = JSON.parse(localStorage.getItem("lastClickedTopics")) || [];
      const updatedTopics = [...new Set([{ name: query, emoji: "" }, ...savedTopics])].slice(0, 9);
      localStorage.setItem("lastClickedTopics", JSON.stringify(updatedTopics));
      navigate(`/${encodeURIComponent(query)}`);
    }
  };

  const handleFileChange = async (file) => {
    if (!file) return;

    if (file.type !== 'application/pdf') {
      notice.open({
        content: (
          <div>
            <div className="d-flex justify-content-between px-2 mb-2">
              <small className="tag">{t('common.error')}</small>
              <strong>{t('home.invalidFileType')}</strong>
            </div>
            <div>{t('home.invalidFileTypeMessage')}</div>
          </div>
        ),
        duration: 5,
        closable: true,
        showProgress: false,
        closeIcon: <IoCloseCircle />,
      });
      return;
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Process the full PDF immediately
    try {
      notice.open({
        content: (
          <div>
            <div className="d-flex justify-content-between px-2 mb-2">
              <small className="tag">{t('home.uploading')}</small>
              <strong>{t('home.uploadingPdf')}</strong>
            </div>
            <div>{t('home.uploadingFile', { fileName: file.name })}</div>
          </div>
        ),
        duration: 2,
        closable: true,
        showProgress: false,
      });

      const result = await processPdf(file);
      navigate(`/file/${result.fileId}`);
    } catch (error) {
      console.error('Error uploading PDF:', error);
      if (error && error.status === 429) {
        notifyAndInviteToSubscribe(notice, error, setShowLoginModal);
      } else {
        notice.open({
          content: (
            <div>
              <div className="d-flex justify-content-between px-2 mb-2">
                <small className="tag">{t('common.error')}</small>
                <strong>{t('home.uploadFailed')}</strong>
              </div>
              <div>{t('home.uploadFailedMessage')}</div>
            </div>
          ),
          duration: 5,
          closable: true,
          showProgress: false,
          closeIcon: <IoCloseCircle />,
        });
      }
    }
  };


  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileChange(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileChange(file);
    }
  };

  const handleInputClick = (e) => {
    // If clicking on the right side (icon area), trigger file input
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const inputWidth = rect.width;
    
    // If click is in the right 20% of the input, trigger file selection
    if (clickX > inputWidth * 0.8) {
      e.preventDefault();
      fileInputRef.current?.click();
    }
  };

  return (
    <div>
      <NotificationContainer>
        {contextHolder}
      </NotificationContainer>
      <Navbar
        isHome={true}
        setShowLoginModal={setShowLoginModal}
        setShowSettingsModal={() => setShowSettingsModal(true)}
      />
      <div id="SearchPage">
        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-content">
            {/* Logo and Title */}
            <div className="logo-container">
              <img src="/logo512.png" alt="Logo" className="logo-modern" />
              <h1 className="hero-title">{t('home.title')}</h1>
            </div>
            
            {/* Subtitle */}
            <p className="hero-subtitle">
              {t('home.subtitle')}
            </p>
            
            {/* Search Section */}
            <div className="search-section">
              <form onSubmit={handleSearch} className="search-form-modern">
                <div 
                  className={`search-input-wrapper ${isDragging ? 'dragging' : ''} ${isFocused ? 'focused' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={handleInputClick}
                >
                  <input
                    type="text"
                    className="search-input-modern"
                    placeholder={t('home.searchPlaceholder')}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    autoFocus
                  />
                  <div className="search-input-icons">
                    {query.trim() ? (
                      <button 
                        type="submit" 
                        className="search-icon-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSearch(e);
                        }}
                      >
                        <FaSearch />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="search-icon-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          fileInputRef.current?.click();
                        }}
                        title={t('home.uploadPdf')}
                      >
                        <FaPaperclip />
                      </button>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf"
                    onChange={handleFileInputChange}
                    style={{ display: 'none' }}
                  />
                </div>
              </form>
              <div className="warning-modern">
                <small>{t('home.aiWarning')}</small>
              </div>
            </div>
          </div>
        </section>

        <div className="content-section">
          <UploadedFilesList />
          <TopicsGrid />
        </div>
      </div>
      {showLoginModal && (
        <LoginModal notification={notice} topic={undefined} />
      )}
      {showSettingsModal && (
        <SettingsModal />
      )}
    </div>
  );
};

export default HomePage;
